/**
 * The report's framing card: what the benchmark is, how it was run, and what it
 * measures. Joan's verbatim copy, rendered from the shared `BENCHMARK_ABOUT`
 * constant so the on-screen report and the Markdown deliverable cannot drift.
 *
 * Collapsible, and collapsed by default: a first-time reader needs the framing,
 * a returning reader wants the data at the top of the page. Structural — not a
 * toggleable analytical section, because it carries no data.
 */
import { benchmarkAbout, type BenchmarkShape } from '@aurora/shared';

export function BenchmarkAbout({
  shape,
}: {
  /**
   * The run's real shape. Joan's methodology sentence states prompt/model/answer
   * counts; without this it falls back to her standard 25/4/100 wording, which
   * would contradict the derived denominator note on any other panel.
   */
  shape?: BenchmarkShape;
}): JSX.Element {
  const blocks = benchmarkAbout(shape);
  return (
    <details className="benchmark-about">
      <summary>
        About this benchmark — what it measures and how it was run
      </summary>
      <div className="benchmark-about-body">
        {blocks.map((block) => (
          <section key={block.heading}>
            <h3 className="section-subhead">{block.heading}</h3>
            {block.paragraphs.map((text) => (
              <p key={text}>{text}</p>
            ))}
            {block.bullets && block.bullets.length > 0 && (
              <ul>
                {block.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
            {(block.closing ?? []).map((text) => (
              <p key={text}>{text}</p>
            ))}
          </section>
        ))}
      </div>
    </details>
  );
}
